import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import {
  MerchantListQuery,
  MerchantRepository,
} from '@app/domain/ports/tpv/merchant.repository';
import {
  AccountMerchantRef,
  TpvMerchant,
  TpvMerchantDetail,
} from '@app/domain/models/tpv/merchant.model';
import { TpvPage } from '@app/domain/models/tpv/tpv-page.model';
import { ReadOptions } from '@app/domain/models/read-options.model';

/** Mock del directorio de comercios para pruebas sin backend (tpvUseMocks). */
@Injectable()
export class MerchantMockDataSource extends MerchantRepository {
  private readonly merchants: TpvMerchant[] = [
    { externalIdMerchant: 'mock-uuid-merchant-1', name: 'Comercio Demo 1', legalName: 'Demo 1 SA de CV', rfc: 'XAXX010101000', status: 'active', unitName: 'UN Centro' },
    { externalIdMerchant: 'mock-uuid-merchant-2', name: 'Comercio Demo 2', legalName: 'Demo 2 SA de CV', rfc: 'XAXX010101001', status: 'inactive', unitName: 'UN Norte' },
    { externalIdMerchant: 'mock-uuid-merchant-3', name: 'Comercio Demo 3', legalName: 'Demo 3 SA de CV', rfc: 'XAXX010101002', status: 'active', unitName: 'UN Sur' },
  ];

  // `options` se acepta por contrato y se descarta: la maqueta no tiene transporte.
  override getMerchants(query: MerchantListQuery = {}, _options?: ReadOptions): Observable<TpvPage<TpvMerchant>> {
    // `search` es el filtro nuevo (contrato 2026-09-29): OR entre nombre, razón
    // social, empresa, unidad y RFC. `merchantName` sigue soportado como back-compat.
    const term = (query.search ?? query.merchantName ?? '').toLowerCase().trim();
    const isSearch = !!query.search;
    let filtered = this.merchants;
    if (term) {
      filtered = filtered.filter((m) => {
        const anyM = m as unknown as { companyName?: string };
        if (isSearch) {
          return (
            m.name.toLowerCase().includes(term) ||
            m.legalName.toLowerCase().includes(term) ||
            (anyM.companyName ?? '').toLowerCase().includes(term) ||
            (m.unitName ?? '').toLowerCase().includes(term) ||
            (m.rfc ?? '').toLowerCase().includes(term)
          );
        }
        return m.name.toLowerCase().includes(term) || m.legalName.toLowerCase().includes(term);
      });
    }
    return of({
      data: filtered,
      total: filtered.length,
      page: query.page ?? 1,
      limit: query.limit ?? 10,
    }).pipe(delay(300));
  }

  override getMerchantsByAccount(_polipayAccountId: number): Observable<AccountMerchantRef[]> {
    return of(
      this.merchants.map((m) => ({ externalIdMerchant: m.externalIdMerchant, status: m.status })),
    ).pipe(delay(300));
  }

  override getMerchantById(externalIdMerchant: string): Observable<TpvMerchantDetail> {
    const base =
      this.merchants.find((m) => m.externalIdMerchant === externalIdMerchant) ?? this.merchants[0];
    return of<TpvMerchantDetail>({
      ...base,
      externalIdMerchant,
      entity: 'Moral',
      represent: 'Representante Demo',
      url: null,
      returnToken: '12345',
      markupDebit: 0,
      markupCredit: 0,
      markupInternacional: 0,
      markupAmex: 0,
      address: {
        country: 'México',
        state: 'CDMX',
        city: 'CDMX',
        town: 'Cuauhtémoc',
        neighborhood: 'Centro',
        cp: '06000',
        street: 'Av. Demo',
        extNumber: '100',
        intNumber: 'A',
        description: '',
      },
      contact: {
        name: 'Contacto Demo',
        rol: 'Administrador',
        email: 'demo@comercio.mx',
        telephonePrincipal: '+525500000000',
        telephoneSecond: '',
        phoneMobile: '+525511111111',
      },
      bank: {
        bank: 'BBVA',
        card: '',
        numAccount: '0000000000',
        clabe: '012180000000000000',
      },
    }).pipe(delay(300));
  }
}
